/**
 * Full ISO-3166 alpha-2 → E.164 calling-code map for the phone-login country picker.
 *
 * MUST stay in sync with the native apps' list — `native-apps/ios/Symphony/Store/CountryCodes.swift`
 * (`countryDial`) and its Android mirror `ui/CountryCodes.kt`. A country missing here locks those users
 * out of phone login: QA #12548799468 was Ukraine (+380) and Lithuania (+370) absent from the web picker
 * while native had them. Keep all three lists equal — same ISO set, same codes.
 *
 * Country names are resolved at render time from the active locale via `Intl.DisplayNames` (mirroring
 * native, whose names come from the OS locale), and flags from the ISO code — so only the ISO→code data
 * lives here.
 */
export const COUNTRY_DIAL: Record<string, string> = {
  AD: '376', AE: '971', AF: '93', AL: '355', AM: '374', AO: '244', AR: '54', AT: '43', AU: '61',
  AZ: '994', BA: '387', BD: '880', BE: '32', BF: '226', BG: '359', BH: '973', BI: '257', BJ: '229',
  BN: '673', BO: '591', BR: '55', BT: '975', BW: '267', BY: '375', BZ: '501', CA: '1', CD: '243',
  CF: '236', CG: '242', CH: '41', CI: '225', CL: '56', CM: '237', CN: '86', CO: '57', CR: '506',
  CU: '53', CV: '238', CY: '357', CZ: '420', DE: '49', DJ: '253', DK: '45', DO: '1', DZ: '213',
  EC: '593', EE: '372', EG: '20', ER: '291', ES: '34', ET: '251', FI: '358', FJ: '679', FM: '691',
  FR: '33', GA: '241', GB: '44', GE: '995', GG: '44', GH: '233', GI: '350', GM: '220', GN: '224',
  GQ: '240', GR: '30', GT: '502', GW: '245', GY: '592', HK: '852', HN: '504', HR: '385', HT: '509',
  HU: '36', ID: '62', IE: '353', IL: '972', IM: '44', IN: '91', IQ: '964', IR: '98', IS: '354',
  IT: '39', JE: '44', JM: '1', JO: '962', JP: '81', KE: '254', KG: '996', KH: '855', KI: '686',
  KM: '269', KP: '850', KR: '82', KW: '965', KZ: '7', LA: '856', LB: '961', LI: '423', LK: '94',
  LR: '231', LS: '266', LT: '370', LU: '352', LV: '371', LY: '218', MA: '212', MC: '377', MD: '373',
  ME: '382', MG: '261', MH: '692', MK: '389', ML: '223', MM: '95', MN: '976', MO: '853', MR: '222',
  MT: '356', MU: '230', MV: '960', MW: '265', MX: '52', MY: '60', MZ: '258', NA: '264', NE: '227',
  NG: '234', NI: '505', NL: '31', NO: '47', NP: '977', NR: '674', NZ: '64', OM: '968', PA: '507',
  PE: '51', PG: '675', PH: '63', PK: '92', PL: '48', PR: '1', PS: '970', PT: '351', PW: '680',
  PY: '595', QA: '974', RO: '40', RS: '381', RU: '7', RW: '250', SA: '966', SB: '677', SC: '248',
  SD: '249', SE: '46', SG: '65', SI: '386', SK: '421', SL: '232', SN: '221', SO: '252', SR: '597',
  SS: '211', SV: '503', SY: '963', SZ: '268', TD: '235', TG: '228', TH: '66', TJ: '992', TL: '670',
  TM: '993', TN: '216', TO: '676', TR: '90', TT: '1', TV: '688', TW: '886', TZ: '255', UA: '380',
  UG: '256', US: '1', UY: '598', UZ: '998', VA: '39', VE: '58', VN: '84', VU: '678', XK: '383',
  YE: '967', ZA: '27', ZM: '260', ZW: '263',
};

export interface CountryOption {
  iso: string;
  /** E.164 calling code without the leading '+', e.g. "380". */
  dial: string;
  /** Localized country name (falls back to the ISO code if the runtime can't resolve one). */
  name: string;
  /** Emoji flag derived from the ISO code. */
  flag: string;
}

/**
 * Best-guess default country ISO from a locale, mirroring native's `deviceCountryIso` (iOS uses the
 * device locale region; Android uses the SIM/network region first, then locale). The web has no SIM, so
 * the browser locale is the analogous signal: an explicit region (e.g. "en-GB" → GB) is used directly,
 * otherwise the locale is maximized to its likely region ("en" → US, "uk" → UA). Falls back to "US" when
 * nothing resolves to a country we carry.
 */
export function defaultCountryIso(locale?: string): string {
  if (locale) {
    try {
      const parsed = new Intl.Locale(locale);
      const region = parsed.region || parsed.maximize().region;
      if (region && COUNTRY_DIAL[region.toUpperCase()]) return region.toUpperCase();
    } catch {
      // fall through to the default
    }
  }
  return 'US';
}

/** Emoji flag for an ISO alpha-2 code (two regional-indicator symbols). */
function isoFlag(iso: string): string {
  return iso
    .toUpperCase()
    .replace(/[A-Z]/g, (c) => String.fromCodePoint(0x1f1e6 + c.charCodeAt(0) - 65));
}

/**
 * Every country as a picker option, sorted by localized name. Names come from `Intl.DisplayNames` in the
 * given locale (like native's OS-locale names); if unavailable the ISO code is shown so the option never
 * renders blank.
 */
export function countryOptions(locale?: string): CountryOption[] {
  let display: Intl.DisplayNames | undefined;
  try {
    display = new Intl.DisplayNames([locale || 'en'], { type: 'region' });
  } catch {
    display = undefined;
  }
  const name = (iso: string): string => {
    try {
      return display?.of(iso) || iso;
    } catch {
      return iso;
    }
  };
  return Object.entries(COUNTRY_DIAL)
    .map(([iso, dial]) => ({ iso, dial, flag: isoFlag(iso), name: name(iso) }))
    .sort((a, b) => a.name.localeCompare(b.name, locale || 'en'));
}
